// src/pages/ShareProfile.js
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Footer from "../Footer";

export default function ShareProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const stateId = location.state?.id || "";
  const { id: routeId } = useParams();

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://tn.goatteachers.com/";

  const stateProfileUrl = location.state?.profileUrl || "";
  const queryId = new URLSearchParams(location.search).get("id");

  const [manualInput, setManualInput] = useState("");

  const derivedIdFromManual = useMemo(() => {
    const match = manualInput.match(/\/tutor\/([^/?#]+)/);
    if (match?.[1]) return match[1];
    if (/^\d+$/.test(manualInput.trim())) return manualInput.trim();
    return "";
  }, [manualInput]);

  const effectiveId =
    stateId ||
    routeId ||
    queryId ||
    stateProfileUrl.match(/\/tutor\/(\d+)/)?.[1] ||
    "" ||
    derivedIdFromManual;

  const profileUrl = useMemo(() => {
    if (stateProfileUrl) return stateProfileUrl;
    if (effectiveId) return `${origin}/tutor/${effectiveId}`;
    return "";
  }, [stateProfileUrl, effectiveId, origin]);

  const [notice, setNotice] = useState("");

  const shareText = (url) => `Need a tutor? This is me on Goatt 🡺 ${url}`;

  const openShare = (platform) => {
    if (!profileUrl) return;
    const text = shareText(profileUrl);
    const u = encodeURIComponent(profileUrl);
    const t = encodeURIComponent(text);
    let link = "";

    switch (platform) {
      case "system":
        if (navigator.share) {
          navigator
            .share({ title: "My Tutor Profile", text, url: profileUrl })
            .catch(() => {});
          return;
        }
        link = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
        break;
      case "whatsapp":
        link = `https://wa.me/?text=${t}`;
        break;
      case "messenger":
        link = `https://www.facebook.com/dialog/send?link=${u}&app_id=000000000000000&redirect_uri=${u}`;
        break;
      case "facebook":
        link = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
        break;
      case "sms":
        link = `sms:?&body=${t}`;
        break;
      case "copy":
        navigator.clipboard
          ?.writeText(profileUrl)
          .then(() => setNotice("Link copied to clipboard"))
          .catch(() => setNotice("❌ Copy failed. Please copy manually."));
        return;
      default:
        return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const disabled = !profileUrl;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-3xl px-4 py-10">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-6 text-center">
              <h2 className="text-2xl font-semibold tracking-tight">
                You’re all set!
              </h2>
              <p className="mt-1 text-slate-600">Your account is created.</p>
            </div>

            <div className="p-6">
              {!profileUrl && (
                <div className="mb-6">
                  <p className="mb-2 text-sm font-medium text-slate-700">
                    Enter your tutor ID or paste your full profile link:
                  </p>
                  <input
                    type="text"
                    value={manualInput}
                    onChange={(e) => {
                      setNotice("");
                      setManualInput(e.target.value);
                    }}
                    placeholder="e.g. 179  or  https://yourdomain.com/tutor/179"
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                  />
                </div>
              )}

              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-700">
                  Would you like to share your tutor profile with friends?
                </p>
                <code className="mt-2 inline-block max-w-full break-words rounded-lg bg-slate-900/90 px-3 py-2 text-xs text-white">
                  {profileUrl || "Profile link will appear here"}
                </code>

                {notice && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {notice}
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => openShare("system")}
                    disabled={disabled}
                    className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Share
                  </button>
                  <button
                    type="button"
                    onClick={() => openShare("whatsapp")}
                    disabled={disabled}
                    className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => openShare("messenger")}
                    disabled={disabled}
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Messenger
                  </button>
                  {/* Uncomment if you want more options */}
                  {/* <button
                    type="button"
                    onClick={() => openShare("facebook")}
                    disabled={disabled}
                    className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Facebook Post
                  </button>
                  <button
                    type="button"
                    onClick={() => openShare("sms")}
                    disabled={disabled}
                    className="rounded-lg bg-slate-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    SMS
                  </button> */}
                  <button
                    type="button"
                    onClick={() => openShare("copy")}
                    disabled={disabled}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Copy Link
                  </button>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/", { replace: true })}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50"
                  >
                    Go to Home →
                  </button>
                  {effectiveId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/tutor/${effectiveId}`)}
                      className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                    >
                      View My Profile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10"></div>
        </div>
      </div>
      <Footer />
    </>
  );
}
